import { useEffect, useState } from 'react'
import { Box, Heading, Text, SimpleGrid, FormControl, FormLabel, Input, Button, Badge, HStack, VStack, useToast, Divider, Switch, Flex } from '@chakra-ui/react'
import { Save, Zap, Feather } from 'lucide-react'
import axios from 'axios'

const TIER_COLORS = {
  free: '#888888',
  standard: '#378ADD',
  premium: '#E87300',
  unlimited: '#FFD700',
}

function PlanEditor({ plan, onSave }) {
  const [form, setForm] = useState({
    name: plan.name,
    price_usd: plan.price_usd,
    books_per_day: plan.books_per_day,
    max_word_count: plan.max_word_count,
    trial_days: plan.trial_days,
    is_active: plan.is_active,
  })
  const [saving, setSaving] = useState(false)
  const toast = useToast()

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const handleSave = async () => {
    setSaving(true)
    try {
      await axios.patch(`/api/plans/admin/${plan.plan_key}`, {
        name: form.name,
        price_usd: parseFloat(form.price_usd),
        books_per_day: parseInt(form.books_per_day),
        max_word_count: parseInt(form.max_word_count),
        trial_days: parseInt(form.trial_days),
        is_active: form.is_active,
      })
      toast({ title: `${plan.name} updated`, status: 'success', duration: 2000 })
      onSave()
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Failed to update', status: 'error', duration: 3000 })
    } finally { setSaving(false) }
  }

  return (
    <Box bg="white" borderRadius="12px" border="1px solid #E2E2E2" p={5} borderTop={`3px solid ${TIER_COLORS[plan.tier] || '#888'}`}>
      <Flex justify="space-between" align="center" mb={4}>
        <HStack spacing={2}>
          <Badge px={2} py={1} borderRadius="6px" fontSize="11px" bg={TIER_COLORS[plan.tier] + '20'} color={TIER_COLORS[plan.tier]}>
            {plan.tier.toUpperCase()}
          </Badge>
          <Text fontSize="14px" fontWeight="500" color="brand.darkBlue">{plan.plan_key}</Text>
        </HStack>
        <HStack spacing={2}>
          <Text fontSize="12px" color="gray.500">Active</Text>
          <Switch isChecked={form.is_active} onChange={e => update('is_active', e.target.checked)} colorScheme="green" size="sm" />
        </HStack>
      </Flex>

      <SimpleGrid columns={2} spacing={3} mb={4}>
        <FormControl>
          <FormLabel fontSize="12px" fontWeight="500" color="gray.600">Plan Name</FormLabel>
          <Input value={form.name} onChange={e => update('name', e.target.value)} size="sm" borderColor="#E2E2E2" _focus={{ borderColor: 'brand.orange' }} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="12px" fontWeight="500" color="gray.600">Price (USD/month)</FormLabel>
          <Input value={form.price_usd} onChange={e => update('price_usd', e.target.value)} type="number" size="sm" borderColor="#E2E2E2" _focus={{ borderColor: 'brand.orange' }} isDisabled={plan.tier === 'free'} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="12px" fontWeight="500" color="gray.600">Books per Day</FormLabel>
          <Input value={form.books_per_day} onChange={e => update('books_per_day', e.target.value)} type="number" size="sm" borderColor="#E2E2E2" _focus={{ borderColor: 'brand.orange' }} />
          <Text fontSize="11px" color="gray.400" mt={1}>Use 999 for unlimited</Text>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="12px" fontWeight="500" color="gray.600">Max Words per Book</FormLabel>
          <Input value={form.max_word_count} onChange={e => update('max_word_count', e.target.value)} type="number" size="sm" borderColor="#E2E2E2" _focus={{ borderColor: 'brand.orange' }} />
        </FormControl>
        {plan.tier === 'free' && (
          <FormControl>
            <FormLabel fontSize="12px" fontWeight="500" color="gray.600">Trial Days</FormLabel>
            <Input value={form.trial_days} onChange={e => update('trial_days', e.target.value)} type="number" size="sm" borderColor="#E2E2E2" _focus={{ borderColor: 'brand.orange' }} />
          </FormControl>
        )}
      </SimpleGrid>

      <Button size="sm" variant="primary" leftIcon={<Save size={13} />} onClick={handleSave} isLoading={saving}>
        Save Changes
      </Button>
    </Box>
  )
}

export default function AdminPlans() {
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [usageStats, setUsageStats] = useState(null)
  const [activeTab, setActiveTab] = useState('platform_author')

  const fetchPlans = () => {
    setLoading(true)
    axios.get('/api/plans/admin/all')
      .then(r => setPlans(r.data.plans || []))
      .finally(() => setLoading(false))
    axios.get('/api/plans/admin/usage')
      .then(r => setUsageStats(r.data))
      .catch(() => {})
  }

  useEffect(() => { fetchPlans() }, [])

  const filteredPlans = plans.filter(p => p.user_type === activeTab)

  return (
    <Box p={8}>
      <Heading size="lg" fontFamily="Georgia, serif" color="brand.darkBlue" mb={2}>Subscription Plans</Heading>
      <Text fontSize="14px" color="gray.500" mb={6}>Manage pricing, book limits and word counts for all plans</Text>

      {usageStats && (
        <SimpleGrid columns={2} spacing={4} mb={6}>
          <Box bg="white" borderRadius="12px" border="1px solid #E2E2E2" p={4}>
            <Text fontSize="12px" color="gray.500" textTransform="uppercase" letterSpacing="0.8px" mb={1}>Books Written Today</Text>
            <Text fontSize="28px" fontWeight="600" fontFamily="Georgia, serif" color="brand.darkBlue">{usageStats.total_books_written_today}</Text>
          </Box>
          <Box bg="white" borderRadius="12px" border="1px solid #E2E2E2" p={4}>
            <Text fontSize="12px" color="gray.500" textTransform="uppercase" letterSpacing="0.8px" mb={1}>Active Users Today</Text>
            <Text fontSize="28px" fontWeight="600" fontFamily="Georgia, serif" color="brand.darkBlue">{usageStats.active_users_today}</Text>
          </Box>
        </SimpleGrid>
      )}

      <HStack bg="white" borderRadius="10px" border="1px solid #E2E2E2" p={1} spacing={1} mb={6} display="inline-flex">
        {[{ key: 'platform_author', label: 'Platform Authors' }, { key: 'external', label: 'Independent Authors' }].map(tab => (
          <Button
            key={tab.key} size="sm" px={5}
            bg={activeTab === tab.key ? 'brand.darkBlue' : 'transparent'}
            color={activeTab === tab.key ? 'white' : 'gray.500'}
            _hover={{ bg: activeTab === tab.key ? 'brand.darkBlue' : 'gray.100' }}
            borderRadius="8px"
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </Button>
        ))}
      </HStack>

      {loading ? (
        <Text color="gray.400">Loading plans...</Text>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {filteredPlans.map(plan => (
            <PlanEditor key={plan.plan_key} plan={plan} onSave={fetchPlans} />
          ))}
        </SimpleGrid>
      )}
    </Box>
  )
}
